/**
 * The conversation log's data: advisor emails grouped into conversations, found by advisor email
 * and time, the conversation open beside the list, "Live" (new emails appear on their own, read
 * every ten seconds), and the per-conversation assistant switch.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { wizardMagicEmailApi, wizardMagicEmailKeys, type ConversationEmail } from '../api/api';

export const LIVE_INTERVAL_MS = 10_000;

export const PERIODS = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'Any time' },
] as const;
export type Period = (typeof PERIODS)[number]['value'];

const startOf = (period: Period) => {
  if (period === 'all') return undefined;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (Number(period) - 1));
  return start.toISOString();
};

export interface ConversationSummary {
  advisorId: string;
  threadKey: string;
  advisorEmail: string;
  /** The newest email's words. */
  excerpt: string;
  lastDateTime: string;
  emails: number;
  latest: ConversationEmail;
}

export function useConversationLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<Period>('7');
  const [live, setLive] = useState(true);
  const email = useDebounce(search.trim(), 400);

  const filters = useMemo(
    () => ({ ...(email ? { email } : {}), ...(startOf(period) ? { from: startOf(period) } : {}) }),
    [email, period],
  );

  const query = useInfiniteQuery({
    queryKey: wizardMagicEmailKeys.conversations(filters),
    queryFn: ({ pageParam }) =>
      wizardMagicEmailApi.fetchConversations({
        ...filters,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: lastPage => lastPage.nextBefore ?? undefined,
    refetchInterval: live ? LIVE_INTERVAL_MS : false,
  });

  // Emails arrive newest first, so the first of each conversation is its latest.
  const conversations = useMemo(() => {
    const byThread = new Map<string, ConversationSummary>();
    for (const row of query.data?.pages.flatMap(page => page.emails) ?? []) {
      const key = `${row.advisorId}|${row.threadKey}`;
      const known = byThread.get(key);
      if (known) known.emails += 1;
      else {
        byThread.set(key, {
          advisorId: row.advisorId,
          threadKey: row.threadKey,
          advisorEmail: row.advisorEmail ?? row.advisorId,
          excerpt: row.excerpt,
          lastDateTime: row.receivedDateTime,
          emails: 1,
          latest: row,
        });
      }
    }
    return [...byThread.values()];
  }, [query.data]);

  const chosen = {
    advisorId: searchParams.get('advisorId'),
    threadKey: searchParams.get('threadKey'),
  };
  // Until one is chosen, the newest conversation is open.
  const selected =
    chosen.advisorId && chosen.threadKey
      ? { advisorId: chosen.advisorId, threadKey: chosen.threadKey }
      : (conversations[0] ?? null);

  const open = useCallback(
    (conversation: Pick<ConversationSummary, 'advisorId' | 'threadKey'>) =>
      setSearchParams(
        params => {
          params.set('advisorId', conversation.advisorId);
          params.set('threadKey', conversation.threadKey);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return {
    search,
    setSearch,
    period,
    setPeriod,
    live,
    setLive,
    query,
    conversations,
    selected,
    open,
    isOpen: (conversation: ConversationSummary) =>
      !!selected &&
      conversation.advisorId === selected.advisorId &&
      conversation.threadKey === selected.threadKey,
  };
}

export function useConversationThread(
  advisorId: string | undefined,
  threadKey: string | undefined,
  live: boolean,
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: wizardMagicEmailKeys.thread(advisorId ?? '', threadKey ?? ''),
    queryFn: () => wizardMagicEmailApi.fetchThread(advisorId!, threadKey!),
    enabled: !!advisorId && !!threadKey,
    refetchInterval: live ? LIVE_INTERVAL_MS : false,
  });

  const switchAssistant = useMutation({
    mutationFn: (assistantOn: boolean) =>
      wizardMagicEmailApi.setThreadAssistant({
        advisorId: advisorId!,
        threadKey: threadKey!,
        assistantOn,
      }),
    onSuccess: ({ off }) => {
      toast.success(
        off
          ? 'Assistant off: the next email goes to a person in Intercom'
          : 'Assistant back on from the next email',
      );
      void queryClient.invalidateQueries({
        queryKey: wizardMagicEmailKeys.thread(advisorId ?? '', threadKey ?? ''),
      });
    },
    onError: error =>
      toast.error(error instanceof Error ? error.message : 'Could not change the assistant'),
  });

  return {
    query,
    thread: query.data,
    setAssistantOn: (on: boolean) => switchAssistant.mutate(on),
    switching: switchAssistant.isPending,
  };
}
